import { inject, Injectable } from "@angular/core";
import { Translation, TranslocoLoader } from "@jsverse/transloco";
import { HttpClient } from "@angular/common/http";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { TenantService } from "./core/services/tenant.service";

// Chargeur Transloco multi-tenant (SP1 Task 2).
//
// - Tenant `vge` : comportement STRICTEMENT historique — une seule requête
//   par langue, réponse rendue telle quelle (invariant : aucun appel
//   d'overlay, pas même un 404).
// - Autres tenants (`vgi`, `vgs`) : la base `./assets/i18n/{lang}.json` est
//   fusionnée avec l'overlay `./assets/i18n/{lang}.{tenant}.json`, l'overlay
//   étant prioritaire clé à clé (opt-in : seules les clés redéfinies changent).
// - Un overlay en erreur (absent, réseau…) ne casse JAMAIS le chargement :
//   fallback silencieux sur la base, simple console.warn.
//
// Merge SUPERFICIEL assumé : les fichiers i18n du projet sont plats (clé =
// texte français pipé |transloco), à l'exception des clés `primeng.*` dont la
// valeur est un objet (dayNames, monthNames…). Un overlay ne doit donc jamais
// redéfinir partiellement une clé `primeng.*` : il remplacerait l'objet entier.
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    private http = inject(HttpClient);
    private tenant = inject(TenantService);

    getTranslation(lang: string): Observable<Translation> {
        const base$ = this.http.get<Translation>(`./assets/i18n/${lang}.json`);
        const tenantKey = this.tenant.key;

        if (tenantKey === 'vge') {
            return base$;
        }

        const overlay$ = this.http.get<Translation>(`./assets/i18n/${lang}.${tenantKey}.json`).pipe(
            catchError((err) => {
                console.warn(`[i18n] Overlay tenant introuvable (${lang}.${tenantKey}.json), base seule utilisée.`, err);
                return of({} as Translation);
            })
        );

        return forkJoin([base$, overlay$]).pipe(
            map(([base, overlay]) => {
                // Garde-fou merge superficiel : les JSON d'overlay ne peuvent pas
                // porter de commentaire — c'est ICI qu'on attrape une clé d'overlay
                // qui écraserait une valeur OBJET de la base (ex. primeng.*) en
                // entier. Une fois par langue au boot, coût négligeable.
                Object.keys(overlay).forEach((k) => {
                    const valeurBase = (base as Record<string, unknown>)[k];
                    if (valeurBase && typeof valeurBase === 'object') {
                        console.warn(`[i18n] L'overlay ${lang}.${tenantKey} écrase la clé objet "${k}" en ENTIER (merge superficiel) — vérifier que c'est voulu.`);
                    }
                });
                return { ...base, ...overlay };
            })
        );
    }
}
